/**
 * ProShip Direct v2 — rule builder. Create, clone, or view a courier rule.
 *
 * One component covers all three because they are the same form in three states, and forking it
 * would mean two places to change every time a condition type is added:
 *   /courier-rules/builder            → create
 *   /courier-rules/builder?clone=id   → create, prefilled from an existing rule
 *   /courier-rules/builder?view=id    → the same layout, read-only, with Clone as the way out
 *
 * There is no edit mode by design. Rules are append-only (see useShippingRules): a rule that has
 * allocated orders explains why those orders went where they did, so it is switched off and
 * cloned rather than rewritten.
 *
 * Conditions are a list rather than a keyed object so two bounds on one field are expressible —
 * order value ≥ 2000 AND ≤ 5000 is two conditions on orderValue, which an object could not hold.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Grid, Typography, Button, TextField, MenuItem, IconButton, Radio, RadioGroup,
  FormControlLabel, Checkbox, Chip, Alert, Divider, InputAdornment, Tooltip,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import GetAppIcon from '@mui/icons-material/GetApp';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import Loader from '../../../common/Loader';
import { pageContainerSx, sectionCardSx, primaryBtnSx, secondaryBtnSx } from '../../../styles/patterns';
import {
  SUBTLE, NAVY, BORDER, WHITE, LIGHT_BG, PRIMARY_BLUE, PURPLE, SUCCESS,
} from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP, RADIUS } from '../../../styles/foundation/spacing';
import { statLabelSx } from '../components/stats';
import CourierPriorityPicker from '../components/CourierPriorityPicker';
import { downloadCsv } from '../data/serviceabilityUpload';
import { useShippingRules, useCourierPool } from '../data/hooks';
import { RULE_CONDITION_TYPES, OPERATORS, OPERATOR_SYMBOL, PRIORITY_STRATEGIES } from '../data/defaults';
import { SETTINGS_PATHS } from '../flags';

const subSx = { fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE };
const nameSx = { fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold, color: NAVY };
const CONTROL_HEIGHT = 34;
const fieldSx = {
  '& .MuiInputBase-root': { fontFamily: FONT, fontSize: SIZE['2xs'], height: CONTROL_HEIGHT },
  '& .MuiInputLabel-root': { fontFamily: FONT, fontSize: SIZE['3xs'] },
};
const radioLabelSx = { '& .MuiFormControlLabel-label': { fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY } };

const STRATEGY_ICONS = {
  CHEAPEST: <SavingsOutlinedIcon />,
  FASTEST: <BoltOutlinedIcon />,
  PACE: <AutoAwesomeOutlinedIcon />,
  PERSONALIZED: <TuneOutlinedIcon />,
};

const strategyCardSx = (selected, disabled) => ({
  p: 1.5,
  height: '100%',
  cursor: disabled ? 'default' : 'pointer',
  bgcolor: selected ? `${PRIMARY_BLUE}0A` : WHITE,
  border: `1px solid ${selected ? PRIMARY_BLUE : BORDER}`,
  borderRadius: RADIUS.md,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.4,
  '& svg': { fontSize: 20, color: selected ? PRIMARY_BLUE : SUBTLE },
});

const conditionCardSx = {
  p: 1.5,
  bgcolor: WHITE,
  border: `1px solid ${BORDER}`,
  borderRadius: RADIUS.md,
  display: 'flex',
  flexDirection: 'column',
  gap: GAP.sm,
};

const SKU_TEMPLATE = ['sku', 'NM-CLS-42', 'NM-SCK-BLK'].join('\n');

/** Human-readable form of one condition, for the preview panel and the summary line. */
const describe = (condition) => {
  const type = RULE_CONDITION_TYPES.find((t) => t.key === condition.type);
  if (!type) return '';
  if (type.kind === 'compare') {
    return `${type.label} ${OPERATOR_SYMBOL[condition.operator] || '='} ${condition.value || '—'}${type.unit === '₹' ? '' : ` ${type.unit}`}`;
  }
  /*
    A window's value is an OBJECT, so it needs its own branch — without one it fell through to
    the last line and interpolated as "[object Object]" in the preview. Named here rather than
    guarded generically because the phrasing matters: the preview is the sentence a merchant
    checks before saving, and it has to say the overnight case out loud.
  */
  if (type.kind === 'between') {
    const { from, to } = condition.value || {};
    if (!from || !to) return `${type.label}: ${from || to ? 'window incomplete' : '—'}`;
    return `${type.label}: ${from} to ${to}${to < from ? ' (next day)' : ''}`;
  }
  if (Array.isArray(condition.value)) {
    const labels = condition.value.map(
      (v) => type.options?.find((o) => o.value === v)?.label || v
    );
    return `${type.label}: ${labels.join(', ') || '—'}`;
  }
  const label = type.options?.find((o) => o.value === condition.value)?.label;
  return `${type.label}: ${label || condition.value || '—'}`;
};

/** The value editor for one condition, chosen by its kind. */
function ConditionValue({ type, condition, onChange, readOnly }) {
  if (type.kind === 'choice') {
    return (
      <RadioGroup
        row
        value={condition.value || ''}
        onChange={(e) => onChange({ value: e.target.value })}
      >
        {type.options.map((o) => (
          <FormControlLabel
            key={o.value}
            value={o.value}
            disabled={readOnly}
            control={<Radio size='small' />}
            label={o.label}
            sx={radioLabelSx}
          />
        ))}
      </RadioGroup>
    );
  }

  if (type.kind === 'multi') {
    const selected = Array.isArray(condition.value) ? condition.value : [];
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {type.options.map((o) => (
          <FormControlLabel
            key={o.value}
            control={
              <Checkbox
                size='small'
                disabled={readOnly}
                checked={selected.includes(o.value)}
                onChange={() =>
                  onChange({
                    value: selected.includes(o.value)
                      ? selected.filter((v) => v !== o.value)
                      : [...selected, o.value],
                  })
                }
              />
            }
            label={o.label}
            sx={radioLabelSx}
          />
        ))}
      </Box>
    );
  }

  /*
    A window takes two clocks, not an operator and a number. The helper line is not decoration:
    a window that ends before it starts is legal and means "across midnight", and nobody guesses
    that from two empty time fields.
  */
  if (type.kind === 'between') {
    const window = condition.value || {};
    const crossesMidnight =
      window.from && window.to && window.to < window.from;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Box sx={{ display: 'flex', gap: GAP.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size='small' label='From' type='time'
            value={window.from || ''}
            disabled={readOnly}
            onChange={(e) => onChange({ value: { ...window, from: e.target.value } })}
            InputLabelProps={{ shrink: true }}
            sx={{ ...fieldSx, width: 150 }}
          />
          <TextField
            size='small' label='To' type='time'
            value={window.to || ''}
            disabled={readOnly}
            onChange={(e) => onChange({ value: { ...window, to: e.target.value } })}
            InputLabelProps={{ shrink: true }}
            sx={{ ...fieldSx, width: 150 }}
          />
        </Box>
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE['3xs'], color: SUBTLE }}>
          {crossesMidnight
            ? `Crosses midnight — matches from ${window.from} through to ${window.to} the next day.`
            : 'Both ends included. A window ending before it starts crosses midnight.'}
        </Typography>
      </Box>
    );
  }

  if (type.kind === 'compare') {
    return (
      <Box sx={{ display: 'flex', gap: GAP.sm, flexWrap: 'wrap' }}>
        <TextField
          select size='small' label='Operator' value={condition.operator || 'GTE'}
          disabled={readOnly}
          onChange={(e) => onChange({ operator: e.target.value })}
          sx={{ ...fieldSx, width: 210 }}
        >
          {OPERATORS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size='small' label='Value' type='number' value={condition.value || ''}
          disabled={readOnly}
          onChange={(e) => onChange({ value: e.target.value })}
          // Weight is in kg, so half a kilo has to be typeable. Integer-only steppers were
          // the reason the unit could not move off grams.
          inputProps={{ step: type.step || 'any', min: 0 }}
          sx={{ ...fieldSx, width: 150 }}
          InputProps={{
            startAdornment: type.unit === '₹' ? <InputAdornment position='start'>₹</InputAdornment> : null,
            endAdornment: type.unit !== '₹' ? <InputAdornment position='end'>{type.unit}</InputAdornment> : null,
          }}
        />
      </Box>
    );
  }

  // list and text share the same field; list adds a CSV path.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.sm }}>
      <TextField
        size='small'
        label={type.kind === 'list' ? 'Comma separated' : type.label}
        placeholder={type.placeholder}
        value={condition.value || ''}
        disabled={readOnly}
        onChange={(e) => onChange({ value: e.target.value })}
        sx={{ ...fieldSx, maxWidth: 460 }}
      />
      {type.kind === 'list' && !readOnly && (
        <Box sx={{ display: 'flex', gap: GAP.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            size='small'
            sx={secondaryBtnSx}
            startIcon={<GetAppIcon sx={{ fontSize: 15 }} />}
            onClick={() => downloadCsv(`${type.key}-template.csv`, SKU_TEMPLATE)}
          >
            Template
          </Button>
          <Button size='small' sx={secondaryBtnSx} startIcon={<UploadFileOutlinedIcon sx={{ fontSize: 15 }} />} component='label'>
            Upload CSV
            <input
              type='file'
              accept='.csv,.txt'
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  const values = String(reader.result || '')
                    .split(/[\r\n,;]+/)
                    .map((v) => v.trim())
                    .filter((v) => v && v.toLowerCase() !== type.key)
                    .join(', ');
                  onChange({ value: values });
                };
                reader.readAsText(file);
                e.target.value = '';
              }}
            />
          </Button>
          <Typography sx={subSx}>CSV only</Typography>
        </Box>
      )}
    </Box>
  );
}

function RuleBuilder() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { rules, createRule, draftFromRule } = useShippingRules();
  const { pool, withheld, isLoading } = useCourierPool();

  const viewId = params.get('view');
  const cloneId = params.get('clone');
  const readOnly = Boolean(viewId);
  const sourceRule = viewId ? rules.find((r) => r.id === viewId) : null;

  const [draft, setDraft] = useState(() => {
    if (viewId) {
      const r = rules.find((x) => x.id === viewId);
      return r ? { name: r.name, conditions: r.conditions, strategy: r.strategy, order: r.order || [] } : null;
    }
    if (cloneId) return draftFromRule(cloneId);
    return { name: '', conditions: [], strategy: 'PACE', order: [] };
  });
  const [error, setError] = useState(null);

  const usedTypes = useMemo(() => (draft?.conditions || []).map((c) => c.type), [draft]);

  if (!draft) {
    return (
      <Box sx={pageContainerSx}>
        <PageTitle>Rule not found</PageTitle>
        <Box sx={sectionCardSx}>
          <Typography sx={subSx}>
            That rule no longer exists.{' '}
            <Box component={Link} to={SETTINGS_PATHS.rules} sx={{ color: PRIMARY_BLUE }}>
              Back to Courier Rules
            </Box>
          </Typography>
        </Box>
      </Box>
    );
  }

  const setCondition = (index, patch) =>
    setDraft((d) => ({
      ...d,
      conditions: d.conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    }));

  const addCondition = () => {
    const next = RULE_CONDITION_TYPES.find((t) => !usedTypes.includes(t.key)) || RULE_CONDITION_TYPES[0];
    setDraft((d) => ({
      ...d,
      conditions: [
        ...d.conditions,
        { type: next.key, operator: next.kind === 'compare' ? 'GTE' : undefined, value: next.kind === 'multi' ? [] : next.kind === 'between' ? {} : '' },
      ],
    }));
  };

  const removeCondition = (index) =>
    setDraft((d) => ({ ...d, conditions: d.conditions.filter((_c, i) => i !== index) }));

  const save = () => {
    if (!draft.name.trim()) {
      setError('Give the rule a name you will recognise in the list.');
      return;
    }
    const incomplete = draft.conditions.some((c) =>
      Array.isArray(c.value) ? c.value.length === 0 : !String(c.value || '').trim()
    );
    if (incomplete) {
      setError('Every condition needs a value, or remove it.');
      return;
    }
    if (draft.strategy === 'PERSONALIZED' && draft.order.length === 0) {
      setError('Personalised allocation needs at least one courier in the priority order.');
      return;
    }
    createRule({ ...draft, name: draft.name.trim() });
    navigate(SETTINGS_PATHS.rules);
  };

  const title = readOnly ? draft.name : cloneId ? 'Clone Rule' : 'Create Rule';

  return (
    <Box sx={pageContainerSx}>
      <PageTitle
        subTitle={
          readOnly
            ? 'A rule cannot be edited — clone it to make a changed version'
            : 'Conditions decide which orders this rule applies to'
        }
      >
        {title}
      </PageTitle>

      <Grid container spacing={2} alignItems='flex-start'>
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.md }}>
            <Box sx={sectionCardSx}>
              <Typography sx={{ ...statLabelSx, mb: 0.75 }}>Rule name</Typography>
              <TextField
                size='small'
                fullWidth
                placeholder='COD heavy to metros'
                value={draft.name}
                disabled={readOnly}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                sx={{ ...fieldSx, maxWidth: 460 }}
              />
            </Box>

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: GAP.sm, mb: 1 }}>
                <Typography sx={statLabelSx}>Conditions</Typography>
                <Typography sx={subSx}>
                  {draft.conditions.length === 0 ? 'none — matches every order' : 'all must match'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.sm }}>
                {draft.conditions.map((condition, i) => {
                  const type = RULE_CONDITION_TYPES.find((t) => t.key === condition.type);
                  return (
                    <Box key={`${condition.type}-${i}`} sx={conditionCardSx}>
                      <Box sx={{ display: 'flex', gap: GAP.sm, alignItems: 'center' }}>
                        <TextField
                          select size='small' label='Condition' value={condition.type}
                          disabled={readOnly}
                          onChange={(e) => {
                            const nextType = RULE_CONDITION_TYPES.find((t) => t.key === e.target.value);
                            setCondition(i, {
                              type: e.target.value,
                              operator: nextType.kind === 'compare' ? 'GTE' : undefined,
                              value: nextType.kind === 'multi' ? [] : nextType.kind === 'between' ? {} : '',
                            });
                          }}
                          sx={{ ...fieldSx, width: 210 }}
                        >
                          {RULE_CONDITION_TYPES.map((t) => (
                            <MenuItem
                              key={t.key}
                              value={t.key}
                              disabled={t.key !== condition.type && usedTypes.includes(t.key) && t.kind !== 'compare'}
                              sx={{ fontFamily: FONT, fontSize: SIZE['2xs'] }}
                            >
                              {t.label}
                            </MenuItem>
                          ))}
                        </TextField>
                        <Box sx={{ flex: 1 }} />
                        {!readOnly && (
                          <Tooltip title='Remove this condition'>
                            <IconButton size='small' onClick={() => removeCondition(i)} aria-label='Remove condition'>
                              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                      {type && (
                        <ConditionValue
                          type={type}
                          condition={condition}
                          readOnly={readOnly}
                          onChange={(patch) => setCondition(i, patch)}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>

              {!readOnly && (
                <Button
                  size='small'
                  onClick={addCondition}
                  startIcon={<AddOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{ ...secondaryBtnSx, mt: draft.conditions.length ? 1 : 0 }}
                >
                  Add condition
                </Button>
              )}
            </Box>

            <Box>
              <Typography sx={{ ...statLabelSx, mb: 1 }}>Allocation</Typography>
              <Grid container spacing={1}>
                {PRIORITY_STRATEGIES.map((s) => (
                  <Grid item xs={6} sm={3} key={s.key}>
                    <Box
                      onClick={() => !readOnly && setDraft((d) => ({ ...d, strategy: s.key }))}
                      sx={strategyCardSx(draft.strategy === s.key, readOnly)}
                    >
                      {STRATEGY_ICONS[s.key]}
                      <Typography sx={nameSx}>{s.label}</Typography>
                      <Typography sx={subSx}>{s.blurb}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {draft.strategy === 'PERSONALIZED' && (
              <Box sx={sectionCardSx}>
                <Typography sx={{ ...statLabelSx, mb: 0.75 }}>Courier priority</Typography>
                <Typography sx={{ ...subSx, mb: 1 }}>
                  Pick from the left, then set the order on the right. Only enabled couriers appear.
                </Typography>
                {isLoading ? (
                  <Loader />
                ) : (
                  <CourierPriorityPicker
                    pool={pool}
                    withheld={withheld}
                    order={draft.order}
                    readOnly={readOnly}
                    onChange={(order) => !readOnly && setDraft((d) => ({ ...d, order }))}
                  />
                )}
              </Box>
            )}

            {error && (
              <Alert severity='warning' onClose={() => setError(null)} sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], py: 0 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: GAP.sm, flexWrap: 'wrap' }}>
              {readOnly ? (
                <>
                  <Button
                    variant='contained'
                    size='small'
                    sx={primaryBtnSx}
                    startIcon={<ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={() => navigate(`${SETTINGS_PATHS.ruleBuilder}?clone=${viewId}`)}
                  >
                    Clone this rule
                  </Button>
                  <Button component={Link} to={SETTINGS_PATHS.rules} size='small' sx={secondaryBtnSx}>
                    Back to rules
                  </Button>
                </>
              ) : (
                <>
                  <Button variant='contained' size='small' sx={primaryBtnSx} onClick={save}>
                    Save rule
                  </Button>
                  <Button component={Link} to={SETTINGS_PATHS.rules} size='small' sx={secondaryBtnSx}>
                    Cancel
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Preview — the rule read back as a sentence, so a mistake is visible before saving. */}
        <Grid item xs={12} md={5}>
          <Box sx={sectionCardSx}>
            <Typography sx={{ ...nameSx, mb: 1.5 }}>
              {draft.name ? `Preview of ${draft.name}` : 'Preview'}
            </Typography>

            <Divider sx={{ borderColor: BORDER, mb: 1.5 }}>
              <Chip
                size='small'
                label='Conditions'
                sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.semibold, bgcolor: `${PURPLE}14`, color: PURPLE }}
              />
            </Divider>

            {draft.conditions.length === 0 ? (
              <Box sx={{ p: 1.25, bgcolor: LIGHT_BG, borderRadius: RADIUS.md }}>
                <Typography sx={subSx}>Every order matches this rule.</Typography>
              </Box>
            ) : (
              draft.conditions.map((condition, i) => (
                <Box key={`preview-${condition.type}-${i}`}>
                  <Box sx={{ p: 1, border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, bgcolor: WHITE }}>
                    <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: NAVY }}>
                      {describe(condition)}
                    </Typography>
                  </Box>
                  {i < draft.conditions.length - 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                      <Chip
                        size='small'
                        label='AND'
                        sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.bold, height: 18, bgcolor: `${PURPLE}14`, color: PURPLE }}
                      />
                    </Box>
                  )}
                </Box>
              ))
            )}

            <Divider sx={{ borderColor: BORDER, my: 1.5 }}>
              <Chip
                size='small'
                label='Then allocate by'
                sx={{ fontFamily: FONT, fontSize: SIZE['4xs'], fontWeight: WEIGHT.semibold, bgcolor: `${PURPLE}14`, color: PURPLE }}
              />
            </Divider>

            <Box sx={{ p: 1, border: `1px solid ${BORDER}`, borderRadius: RADIUS.md, bgcolor: WHITE }}>
              <Typography sx={nameSx}>
                {PRIORITY_STRATEGIES.find((s) => s.key === draft.strategy)?.label}
              </Typography>
              {draft.strategy === 'PERSONALIZED' && (
                <Typography sx={{ ...subSx, mt: 0.25 }}>
                  {draft.order.length
                    ? draft.order
                        .map((key, i) => `P${i + 1} ${pool.find((p) => p.key === key)?.name || 'unavailable'}`)
                        .join(' · ')
                    : 'No courier order set yet'}
                </Typography>
              )}
            </Box>

            {sourceRule && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${BORDER}` }}>
                <Typography sx={subSx}>
                  Created by {sourceRule.createdBy} ·{' '}
                  <Box component='span' sx={{ color: sourceRule.enabled ? SUCCESS : SUBTLE }}>
                    {sourceRule.enabled ? 'Enabled' : 'Disabled'}
                  </Box>
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default withLayout(RuleBuilder);
